/**
 * sl-9qm.4.3: KPICard Component
 *
 * Single metric value with label and optional trend indicator.
 * Aggregation (COUNT/SUM/AVG/MIN/MAX) computed client-side.
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type { ViewComponent } from '../../../types/view-component.types';
import * as styles from '../styles.css';

const aggregationTypes = ['count', 'sum', 'avg', 'min', 'max'] as const;
type AggregationType = (typeof aggregationTypes)[number];

const configSchema = z.object({
  aggregationType: z.enum(aggregationTypes).default('count'),
  valueColumn: z.string().default(''),
  label: z.string().default('Metric'),
  format: z.enum(['number', 'currency', 'percent']).default('number'),
  showTrend: z.boolean().default(false),
});

function aggregate(values: number[], type: AggregationType): number {
  if (values.length === 0) return 0;
  switch (type) {
    case 'count':
      return values.length;
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
  }
}

function formatValue(value: number, format: string): string {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(value);
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    default:
      return new Intl.NumberFormat('de-DE').format(value);
  }
}

export const KPICardComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.KPI_CARD,
  type: 'data',
  name: 'KPI Card',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    '#'
  ),
  capabilities: [
    {
      role: 'primary',
      description: 'Data rows for aggregation',
      required: true,
      requirements: [
        { field: 'value', acceptedTypes: ['number'], required: false },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    aggregationType: 'count',
    valueColumn: '',
    label: 'Metric',
    format: 'number',
    showTrend: false,
  },
  render({ config, data }) {
    const { aggregationType, valueColumn, label, format } = config as z.infer<
      typeof configSchema
    >;

    const values = data
      .map(row => {
        const v = valueColumn ? row[valueColumn] : Object.values(row)[0];
        return typeof v === 'number' ? v : parseFloat(String(v));
      })
      .filter(v => !isNaN(v));

    const result = aggregate(values, aggregationType as AggregationType);

    return createElement(
      'div',
      {
        style: {
          padding: 'var(--sl-app-padding-lg)',
          borderRadius: 'var(--sl-app-radius-md)',
          border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
          background: 'var(--streamlain-v2-layer-background-primary)',
          textAlign: 'center' as const,
        },
        role: 'figure',
        'aria-label': `${label}: ${formatValue(result, format)}`,
      },
      createElement(
        'div',
        {
          'data-sl-field': 'label',
          style: {
            fontSize: 'var(--sl-app-font-size-xs)',
            color: 'var(--streamlain-v2-text-secondary)',
            marginBottom: 'var(--sl-app-gap-sm)',
            textTransform: 'uppercase' as const,
            letterSpacing: 1,
          },
        },
        label
      ),
      createElement(
        'div',
        {
          'data-sl-field': 'value',
          style: {
            fontSize: 'var(--sl-app-font-size-display)',
            fontWeight: 700,
            color: 'var(--streamlain-v2-text-primary)',
          },
        },
        formatValue(result, format)
      )
    );
  },
  palettePreview() {
    return createElement('div', { className: styles.palettePreview }, '# KPI');
  },
};
